import { MAIN_MARKETPLACE_LISTING_URL } from '../constants/siteClosing';
import { hostnameFromUrl, isLocalDevHost } from './hostGuards';
import type { ThemeProps } from './theme';

const HTTPS_PROTOCOL = 'https:';
const WWW_PREFIX = 'www.';

type ClosingTheme = Pick<ThemeProps, 'siteClosing' | 'closingRedirectUrl'>;

interface ClosingSiteRedirectCheck {
  currentHost: string;
  targetUrl: string | null;
  isEmbedded: boolean;
}

function isHttpsUrl(url: string | null | undefined): url is string {
  if (!url) {
    return false;
  }
  try {
    return new URL(url).protocol === HTTPS_PROTOCOL;
  } catch {
    return false;
  }
}

function isSameSiteHost(hostA: string, hostB: string): boolean {
  return hostA === hostB || `${WWW_PREFIX}${hostA}` === hostB || `${WWW_PREFIX}${hostB}` === hostA;
}

// Persisted themes (zustand "theme", localStorage "whitelabel_theme") can outlive a restore,
// so only a theme fetched in this page load may start the redirect.
export function resolveClosingRedirectUrl(
  theme: ClosingTheme | undefined,
  { isThemeFromServer }: { isThemeFromServer: boolean }
): string | null {
  if (!isThemeFromServer || theme?.siteClosing !== true) {
    return null;
  }
  return isHttpsUrl(theme.closingRedirectUrl) ? theme.closingRedirectUrl : MAIN_MARKETPLACE_LISTING_URL;
}

export function shouldRedirectClosingSite({ currentHost, targetUrl, isEmbedded }: ClosingSiteRedirectCheck): boolean {
  if (!targetUrl || isEmbedded || isLocalDevHost(currentHost)) {
    return false;
  }
  const targetHost = hostnameFromUrl(targetUrl);
  if (!targetHost) {
    return false;
  }
  // A same-host target would loop if the marketplace were ever itself a closing TVA site.
  return !isSameSiteHost(targetHost, currentHost);
}
